/**
 * Question generator — creates quiz questions from timeline events and map markers.
 * Uses template patterns to produce questions with known answers.
 */

import { formatYear, formatDate } from '../../timeline/utils/dateUtils';

/**
 * Generate questions from a set of timeline events and map markers.
 * @param {object[]} timelineEvents - Array of timeline events
 * @param {object[]} mapMarkers - Array of map markers
 * @returns {object[]} Array of { question, answer, source, type, tags }
 */
export function generateQuestions(timelineEvents = [], mapMarkers = []) {
  const questions = [];

  for (const event of timelineEvents) {
    const tags = event.tags || [];

    // One date question per event — different phrasing for ranged vs point
    if (event.startYear) {
      if (event.endYear) {
        // Ranged event — include full dates if month/day available
        const startStr = formatDate(event.startYear, event.startMonth, event.startDay);
        const endStr = formatDate(event.endYear, event.endMonth, event.endDay);
        questions.push({
          question: `What dates define the start and end of the period: "${event.title}"?`,
          answer: `${startStr} – ${endStr}`,
          source: 'timeline',
          type: 'date',
          tags,
          eventId: event.id,
        });
      } else {
        // Point event — use full date if day/month available
        let answer;
        if (event.startMonth && event.startDay) {
          answer = formatDate(event.startYear, event.startMonth, event.startDay);
        } else if (event.startMonth) {
          answer = formatDate(event.startYear, event.startMonth);
        } else {
          answer = formatYear(event.startYear);
        }
        questions.push({
          question: `When did "${event.title}" happen?`,
          answer,
          source: 'timeline',
          type: 'date',
          tags,
          eventId: event.id,
        });
      }
    }
    
    // Summary question — recall what happened
    if (event.summary) {
      questions.push({
        question: `What happened during "${event.title}"?`,
        answer: event.summary,
        source: 'timeline',
        type: 'summary',
        tags,
        eventId: event.id,
      });
    }
  }

  for (const marker of mapMarkers) {
    const tags = marker.tags || [];

    // Location question
    if (marker.lat && marker.lng) {
      questions.push({
        question: `Where is "${marker.title}" located? (What region/country?)`,
        answer: marker.summary || `${marker.lat.toFixed(2)}, ${marker.lng.toFixed(2)}`,
        source: 'map',
        type: 'location',
        tags,
        markerId: marker.id,
      });
    }

    // Significance question
    if (marker.summary) {
      questions.push({
        question: `What happened at, or what is significant about ${marker.title}?`,
        answer: marker.summary,
        source: 'map',
        type: 'significance',
        tags,
        markerId: marker.id,
      });
    }
  }

  return questions;
}

/**
 * Filter questions by tags (AND logic — all selected tags must be present).
 */
export function filterByTags(questions, selectedTags) {
  if (!selectedTags || selectedTags.length === 0) return questions;
  return questions.filter(q =>
    selectedTags.every(tag => q.tags.includes(tag))
  );
}

/**
 * Filter questions by type.
 */
export function filterByType(questions, selectedTypes) {
  if (!selectedTypes || selectedTypes.length === 0) return questions;
  return questions.filter(q => selectedTypes.includes(q.type));
}

/**
 * Shuffle an array (Fisher-Yates).
 */
export function shuffle(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Get all unique tags from a set of questions.
 */
export function getAllTags(questions) {
  const tagSet = new Set();
  questions.forEach(q => q.tags.forEach(t => tagSet.add(t)));
  return [...tagSet].sort();
}

/**
 * Get all unique question types.
 */
export function getAllTypes(questions) {
  return [...new Set(questions.map(q => q.type))].sort();
}
